import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import type { UpsellBillingMode } from "@/hooks/use-upsells";

interface Props {
  billingMode: UpsellBillingMode;
  onChange: (mode: UpsellBillingMode) => void;
  readOnly: boolean;
  /** VIB-994: mersalget har allerede timeføring eller faktura — modus kan ikke endres. */
  locked: boolean;
}

const OPTIONS: { value: UpsellBillingMode; label: string; hint: string }[] = [
  {
    value: "fixed",
    label: "Fastpris",
    hint: "Avtalt sum for linjene, med overhead og rabatt.",
  },
  {
    value: "tm",
    label: "Medgått tid + materiell (T&M)",
    hint: "Fakturers løpende fra loggede timer og bilag. Linjene under er kun et valgfritt estimat til styring.",
  },
];

/**
 * VIB-994: velg mersalgets faktureringsform. Bak `feature_tm_upsell` —
 * kalleren (UpsellEditDialog) avgjør om komponenten i det hele tatt vises.
 *
 * Låses (kan ikke endres) så snart mersalget har timeføring eller faktura —
 * se `src/lib/upsell-billing-mode-lock.ts`. Vernet håndheves autoritativt i
 * mutation-laget (`use-upsell-mutations.ts`); `locked` her er kun UI-hint
 * avledet fra allerede lastet data, så knappen kan disables uten en ekstra
 * rundtrip.
 */
export function UpsellBillingModeFields({ billingMode, onChange, readOnly, locked }: Props) {
  const disabled = readOnly || locked;
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-base">Faktureringsform</h3>
          {locked && (
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <SrIcon name="lock" className="w-3 h-3" />
              Låst — timeføring/faktura registrert
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              aria-pressed={billingMode === opt.value}
              className={cn(
                "text-left rounded-md border p-3 transition-colors",
                billingMode === opt.value
                  ? "border-primary bg-primary/5"
                  : "border-input bg-background hover:bg-muted/40",
                disabled && "opacity-60 cursor-not-allowed",
              )}
            >
              <Label className="cursor-pointer">{opt.label}</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">{opt.hint}</p>
            </button>
          ))}
        </div>
        {billingMode === "tm" && (
          <p className="text-[11px] text-muted-foreground">
            T&M-mersalg fakturers løpende fra loggede timer og bilag på linjene under —
            linjenes beløp er kun et valgfritt estimat til intern styring, ikke fakturagrunnlaget.
            Se «Faktura»-fanen for å opprette T&M-faktura (krever at mersalget er godkjent).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
